import React from 'react';
import { TransactionTimeline as TransactionTimelineType } from '../../../utils/types';
import { TransactionData } from '../utils/transactionUtils';
import { formatDateToDisplayShortLocalMonth } from '../../../utils/utils';
import { getPaymentMode } from '../utils/transactionUtils';
import { TimelineStep } from './TimelineStep';

interface TransactionTimelineProps {
  timeline: TransactionTimelineType | undefined;
  transaction: TransactionData | null;
  isLoading: boolean;
}

export const TransactionTimeline: React.FC<TransactionTimelineProps> = ({
  timeline,
  transaction,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <div className='space-y-4'>
        <div className='h-6 w-48 bg-gray-200 animate-pulse rounded-md'></div>
        <div className='h-6 w-64 bg-gray-200 animate-pulse rounded-md'></div>
      </div>
    );
  }

  if (!timeline || !transaction) {
    return null;
  }

  const status = transaction.status;

  // Case 1: Failed transaction → only "Payment method charged"
  if (status === 'failed') {
    const failedStep = {
      title: 'Payment method charged',
      date: timeline?.paymentSuccess
        ? formatDateToDisplayShortLocalMonth(timeline.paymentSuccess)
        : null,
    };

    return (
      <div key={failedStep.title}>
        <TimelineStep
          title={failedStep.title}
          date={failedStep.date}
          isLast={true}
          status={status}
        />
      </div>
    );
  }

  // Case 2: Success or in-progress → build progressive timeline
  const steps: { title: string; date: string | null; isExpected?: boolean }[] = [];

  // Step 1: Always show
  steps.push({
    title: 'Payment method charged',
    date: timeline?.paymentSuccess
      ? formatDateToDisplayShortLocalMonth(timeline.paymentSuccess)
      : null,
  });

  // Step 2: Only show if card was charged
  if (timeline?.paymentSuccess) {
    steps.push({
      title: timeline?.batchCreatedAt
        ? 'Funds received'
        : `Awaiting funds from ${
            transaction?.cardType
              ? `${transaction.cardType} card`
              : getPaymentMode(
                  transaction?.paymentMethodName || '',
                  transaction?.cardCompany,
                  transaction?.cardType
                ) || 'payment method'
          }`,
      date: timeline?.batchCreatedAt
        ? formatDateToDisplayShortLocalMonth(timeline.batchCreatedAt)
        : null,
    });
  }

  // Step 3: Funds sent to landlord. Until sent, show the expected date:
  // batch creation time if a batch exists, else the expectedPaymentSendDate
  // (the latter only once the card is charged).
  const expectedSendDate =
    timeline?.batchCreatedAt ||
    (timeline?.paymentSuccess ? transaction.expectedPaymentSendDate : null);
  if (timeline?.batchCreatedAt || expectedSendDate) {
    steps.push(
      timeline?.paymentSendToLandlord
        ? {
            title: 'Funds sent to landlord',
            date: formatDateToDisplayShortLocalMonth(timeline.paymentSendToLandlord),
          }
        : {
            title: timeline?.batchCreatedAt
              ? 'Disbursement in progress'
              : 'Funds will be sent to landlord on',
            date: expectedSendDate ? formatDateToDisplayShortLocalMonth(expectedSendDate) : null,
            isExpected: true,
          }
    );
  }

  if (timeline?.paymentReportedToEquifax) {
    steps.push({
      title: 'Reported to Equifax',
      date: formatDateToDisplayShortLocalMonth(timeline.paymentReportedToEquifax),
    });
  }

  return (
    <div>
      {steps.map((step, index, arr) => (
        <div key={step.title + (step.date || index)}>
          <TimelineStep
            title={step.title}
            date={step.date}
            isLast={index === arr.length - 1}
            status={status}
            isExpected={step.isExpected}
          />
        </div>
      ))}
    </div>
  );
};
